import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Form, Modal, Spinner, Badge } from 'react-bootstrap';
import { galleryAPI } from '../services/api';
import { API_URL } from '../config';

interface GalleryItem {
  id: number;
  title: string;
  description?: string;
  file_name: string;
  file_path: string;
  file_type: string;
  upload_date: string;
}

const Gallery: React.FC = () => {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('');
  const [viewModal, setViewModal] = useState<GalleryItem | null>(null);

  useEffect(() => {
    loadGallery();
  }, [filterType]);

  const loadGallery = async () => {
    try {
      setLoading(true);
      const response = await galleryAPI.getAll(filterType);
      setItems(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await galleryAPI.delete(String(id));
        loadGallery();
      } catch (err) {
        setError('Failed to delete item');
      }
    }
  };

  const getFileUrl = (filePath: string) => {
    if (!filePath) return '';

    // If it's already a full URL, return as is
    if (filePath.startsWith('http')) {
      return filePath;
    }

    // Clean the path - remove 'uploads/' prefix if present
    const cleanPath = filePath.replace(/^uploads\//, '');

    // In production, use relative path with proxy
    // In development, use full API URL
    if (process.env.NODE_ENV === 'production') {
      return `/api/uploads/${cleanPath}`;
    } else {
      return `${API_URL}/uploads/${cleanPath}`;
    }
  };

  const handleView = (item: GalleryItem) => {
    setViewModal(item);
  };

  const closeViewModal = () => {
    setViewModal(null);
  };

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
      <Container>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ 
            color: '#00579a', 
            fontWeight: 600,
            fontSize: '28px',
            margin: 0
          }}>
            Gallery
          </h1>
          <Link 
            to="/gallery/new" 
            className="btn"
            style={{
              backgroundColor: '#00579a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 20px',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,87,154,0.2)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#004a85'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#00579a'}
          >
            + Upload New Item
          </Link>
        </div>

        {error && (
          <Alert 
            variant="danger" 
            dismissible 
            onClose={() => setError('')}
            style={{
              borderRadius: '6px',
              border: '1px solid #ffccc7',
              backgroundColor: '#fff2f0',
              marginBottom: '24px'
            }}
          >
            {error}
          </Alert>
        )}

        <Card style={{ 
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
          marginBottom: '24px',
          padding: '20px'
        }}>
          <Form.Group>
            <Form.Label style={{ 
              fontSize: '14px',
              fontWeight: 600,
              color: '#262626',
              marginBottom: '8px'
            }}>
              Filter by Type
            </Form.Label>
            <Form.Select 
              value={filterType} 
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                borderRadius: '6px',
                border: '1px solid #d9d9d9',
                padding: '8px 12px',
                fontSize: '14px',
                maxWidth: '300px'
              }}
            >
              <option value="">All Types</option>
              <option value="photo">Photos</option>
              <option value="video">Videos</option>
            </Form.Select>
          </Form.Group>
        </Card>

        {loading ? (
          <div className="text-center" style={{ padding: '60px 0' }}>
            <Spinner 
              animation="border" 
              style={{ color: '#00579a', width: '40px', height: '40px' }}
            />
            <p style={{ marginTop: '16px', color: '#8c8c8c' }}>Loading gallery...</p>
          </div>
        ) : (
          <Row>
            {items.length === 0 ? (
              <Col>
                <Card style={{ 
                  border: 'none',
                  borderRadius: '8px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                  padding: '40px',
                  textAlign: 'center'
                }}>
                  <p style={{ color: '#8c8c8c', margin: 0 }}>
                    No gallery items found. Click "Upload New Item" to add one.
                  </p>
                </Card>
              </Col>
            ) : (
              items.map((item) => (
                <Col key={item.id} md={6} lg={4} className="mb-4">
                  <Card style={{ 
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    height: '100%'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.08), 0 2px 12px rgba(0,0,0,0.12)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  >
                    {item.file_type === 'photo' ? (
                      <Card.Img 
                        variant="top" 
                        src={getFileUrl(item.file_path)} 
                        alt={item.title || 'Gallery image'} 
                        style={{ 
                          height: '200px', 
                          objectFit: 'cover', 
                          cursor: 'pointer',
                          transition: 'opacity 0.2s ease'
                        }}
                        onClick={() => handleView(item)}
                        onMouseOver={(e) => e.currentTarget.style.opacity = '0.85'}
                        onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                      />
                    ) : (
                      <div 
                        style={{ 
                          height: '200px', 
                          cursor: 'pointer',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          transition: 'opacity 0.2s ease'
                        }}
                        onClick={() => handleView(item)}
                        onMouseOver={(e) => e.currentTarget.style.opacity = '0.85'}
                        onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        <div className="text-center">
                          <div style={{ fontSize: '48px', marginBottom: '8px' }}>▶</div>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>Click to view video</p>
                        </div>
                      </div>
                    )}
                    <Card.Body style={{ padding: '16px' }}>
                      <Card.Title style={{ 
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#262626',
                        marginBottom: '8px'
                      }}>
                        {item.title || 'Untitled'}
                      </Card.Title>
                      {item.description && (
                        <Card.Text style={{ 
                          color: '#8c8c8c',
                          fontSize: '13px',
                          marginBottom: '12px',
                          lineHeight: '1.6'
                        }}>
                          {item.description.substring(0, 100)}
                          {item.description.length > 100 && '...'}
                        </Card.Text>
                      )}
                      <div style={{ marginBottom: '12px' }}>
                        <Badge 
                          style={{
                            backgroundColor: item.file_type === 'photo' ? '#e6f4ff' : '#fff7e6',
                            color: item.file_type === 'photo' ? '#ffffffff' : '#d46b08',
                            fontWeight: 500,
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            marginRight: '8px'
                          }}
                        >
                          {item.file_type === 'photo' ? '📷 Photo' : '🎥 Video'}
                        </Badge>
                        <small style={{ color: '#8c8c8c', fontSize: '12px' }}>
                          {new Date(item.upload_date).toLocaleDateString()}
                        </small>
                      </div>
                      <div className="d-flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleView(item)}
                          style={{
                            backgroundColor: '#00579a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 12px',
                            fontSize: '13px',
                            fontWeight: 500,
                            flex: 1
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#004a85'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#00579a'}
                        >
                          View
                        </Button>
                        <Link 
                          to={`/gallery/edit/${item.id}`} 
                          className="btn btn-sm"
                          style={{
                            backgroundColor: 'white',
                            color: '#00579a',
                            border: '1px solid #00579a',
                            borderRadius: '4px',
                            padding: '4px 12px',
                            fontSize: '13px',
                            fontWeight: 500,
                            flex: 1,
                            textAlign: 'center'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e6f4ff'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          Edit
                        </Link>
                        <Button 
                          size="sm" 
                          onClick={() => handleDelete(item.id)}
                          style={{
                            backgroundColor: 'white',
                            color: '#ff4d4f',
                            border: '1px solid #ff4d4f',
                            borderRadius: '4px',
                            padding: '4px 12px',
                            fontSize: '13px',
                            fontWeight: 500,
                            flex: 1
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fff1f0'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          Delete
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        )}

        {/* View Modal */}
        <Modal 
          show={viewModal !== null} 
          onHide={closeViewModal} 
          size="lg" 
          centered
          style={{ zIndex: 1050 }}
        >
          <Modal.Header 
            closeButton
            style={{
              borderBottom: '1px solid #f0f0f0',
              padding: '16px 24px'
            }}
          >
            <Modal.Title style={{ 
              fontSize: '18px',
              fontWeight: 600,
              color: '#262626'
            }}>
              {viewModal?.title || 'Gallery Item'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '24px' }}>
            {viewModal && (
              <>
                {viewModal.file_type === 'photo' ? (
                  <img 
                    src={getFileUrl(viewModal.file_path)} 
                    alt={viewModal.title || 'Gallery image'} 
                    style={{ 
                      width: '100%', 
                      height: 'auto',
                      borderRadius: '6px'
                    }}
                  />
                ) : (
                  <video 
                    controls 
                    style={{ 
                      width: '100%', 
                      height: 'auto',
                      borderRadius: '6px'
                    }}
                    src={getFileUrl(viewModal.file_path)}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
                {viewModal.description && (
                  <div style={{ 
                    marginTop: '20px',
                    padding: '16px',
                    backgroundColor: '#fafafa',
                    borderRadius: '6px'
                  }}>
                    <h6 style={{ 
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#262626',
                      marginBottom: '8px'
                    }}>
                      Description:
                    </h6>
                    <p style={{ 
                      fontSize: '14px',
                      color: '#595959',
                      margin: 0,
                      lineHeight: '1.6'
                    }}>
                      {viewModal.description}
                    </p>
                  </div>
                )}
                <div style={{ 
                  marginTop: '16px',
                  padding: '12px 16px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px'
                }}>
                  <small style={{ color: '#8c8c8c', fontSize: '13px' }}>
                    <strong style={{ color: '#595959' }}>Filename:</strong> {viewModal.file_name}<br />
                    <strong style={{ color: '#595959' }}>Type:</strong> {viewModal.file_type}<br />
                    <strong style={{ color: '#595959' }}>Uploaded:</strong> {new Date(viewModal.upload_date).toLocaleDateString()}
                  </small>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer style={{ 
            borderTop: '1px solid #f0f0f0',
            padding: '16px 24px'
          }}>
            <Button 
              onClick={closeViewModal}
              style={{
                backgroundColor: 'white',
                color: '#595959',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '8px 20px',
                fontWeight: 500,
                fontSize: '14px'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#00579a';
                e.currentTarget.style.color = '#00579a';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#d9d9d9';
                e.currentTarget.style.color = '#595959';
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default Gallery;